import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import {
  ArrowLeft, User, Mail, Phone, Briefcase, ShieldCheck, Activity,
  ClipboardList, Zap, CheckCircle2, Clock, PauseCircle, XCircle,
  TrendingUp, Percent, Calendar, LogIn,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const permLabel = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function KPI({ icon: Icon, label, value, tint, suffix }) {
  return (
    <div className="kpi-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="mt-2 font-heading text-2xl font-extrabold tabular">{value}{suffix}</div>
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon size={16} /></div>
      </div>
    </div>
  );
}

export default function EmployeeProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("overview");
  const [employees, setEmployees] = useState([]);
  const [reassign, setReassign] = useState({});

  const load = async () => {
    try {
      const [r, eResp] = await Promise.all([
        api.get(`/employees/${id}/profile`),
        api.get(`/employees`),
      ]);
      setData(r.data);
      setEmployees(eResp.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load profile");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const reassignLead = async (lead, newEmpId) => {
    if (!newEmpId || newEmpId === id) return;
    await api.patch(`/leads/${lead.id}`, { assigned_to_id: newEmpId });
    load();
  };

  if (err) return <div className="card p-8 text-red-600">{err}</div>;
  if (!data) return <div>Loading profile…</div>;

  const { employee, kpi, service_breakdown, currently_working, leads, login_history, recent_activity } = data;

  const byStatus = {
    "New Case": leads.filter((l) => l.status === "New Case"),
    "Work In Progress": leads.filter((l) => l.status === "Work In Progress"),
    "On Hold": leads.filter((l) => l.status === "On Hold"),
    "Completed": leads.filter((l) => l.status === "Completed"),
    "Rejected": leads.filter((l) => l.status === "Rejected"),
  };

  return (
    <div className="space-y-6" data-testid="employee-profile-page">
      <Link to="/employees" className="text-sm link-blue inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to employees</Link>

      {/* Header */}
      <div className="card p-6">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="w-20 h-20 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading font-extrabold text-3xl">
            {employee.name[0]}
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-3xl font-extrabold">{employee.name}</h1>
              {employee.disabled ? <span className="badge badge-red">Disabled</span> : <span className="badge badge-green">Active</span>}
            </div>
            <div className="text-sm text-muted-foreground mt-1 font-mono">@{employee.username}</div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2 max-w-lg text-sm">
              <div className="flex items-center gap-2"><Mail size={14} className="text-muted-foreground" /> {employee.email || "—"}</div>
              <div className="flex items-center gap-2"><Phone size={14} className="text-muted-foreground" /> {employee.phone || "—"}</div>
            </div>
          </div>
          <div className="flex flex-col gap-2 min-w-[220px]">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><ShieldCheck size={12} /> Permissions</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(employee.permissions || []).length === 0 && <span className="text-xs text-muted-foreground">No perms</span>}
                {(employee.permissions || []).map((p) => <span key={p} className="badge badge-blue text-[10px]">{permLabel(p)}</span>)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><Briefcase size={12} /> Services</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(employee.services || []).length === 0 ? <span className="badge badge-gray text-[10px]">All</span> :
                  (employee.services || []).map((s) => <span key={s} className="badge badge-green text-[10px]">{s}</span>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance KPIs */}
      <div>
        <h3 className="font-heading font-bold text-lg mb-3">Performance Dashboard</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="employee-kpis">
          <KPI icon={ClipboardList} label="Assigned" value={kpi.total_assigned} tint="bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" />
          <KPI icon={Zap} label="Contacted" value={kpi.contacted} tint="bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300" />
          <KPI icon={CheckCircle2} label="Converted" value={kpi.converted} tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" />
          <KPI icon={Clock} label="Pending" value={kpi.pending} tint="bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300" />
          <KPI icon={PauseCircle} label="Follow-up" value={kpi.follow_up} tint="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" />
          <KPI icon={XCircle} label="Rejected" value={kpi.rejected} tint="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <KPI icon={Percent} label="Conversion Rate" value={kpi.conversion_rate} suffix="%" tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" />
          <KPI icon={Calendar} label="Today (updated)" value={kpi.daily} tint="bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300" />
          <KPI icon={TrendingUp} label="This Week" value={kpi.weekly} tint="bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300" />
          <KPI icon={TrendingUp} label="This Month" value={kpi.monthly} tint="bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300" />
        </div>
      </div>

      {/* Currently working */}
      {currently_working && (
        <div className="card p-5 border-l-4 !border-l-amber-500">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Currently working on</div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Link to={`/leads/${currently_working.id}`} className="font-mono font-bold text-lg link-blue">{currently_working.lead_number}</Link>
            <span className="font-semibold">{currently_working.client_name}</span>
            <StatusBadge status={currently_working.status} />
            <span className="badge badge-blue">{currently_working.service_type}</span>
            <span className="text-xs text-muted-foreground">Last updated: {new Date(currently_working.updated_at).toLocaleString()}</span>
          </div>
        </div>
      )}

      {/* Service breakdown chart */}
      {service_breakdown.length > 0 && (
        <div className="card p-5">
          <h3 className="font-heading font-bold text-lg mb-3">Leads by Service</h3>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={service_breakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="value" fill="#0052CC" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border flex gap-1 flex-wrap">
        {[
          ["overview", "All Leads"],
          ["New Case", `Pending (${byStatus["New Case"].length})`],
          ["Work In Progress", `In Progress (${byStatus["Work In Progress"].length})`],
          ["On Hold", `Follow-up (${byStatus["On Hold"].length})`],
          ["Completed", `Converted (${byStatus["Completed"].length})`],
          ["Rejected", `Rejected (${byStatus["Rejected"].length})`],
          ["logins", "Login History"],
          ["activity", "Recent Activity"],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`emp-tab-${key}`}
            className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {["overview", "New Case", "Work In Progress", "On Hold", "Completed", "Rejected"].includes(tab) && (
        <div className="card overflow-hidden">
          <table className="table-clean">
            <thead>
              <tr>
                <th>Lead ID</th><th>Service</th><th>Client</th><th>Status</th><th>SLA</th><th>Updated</th>
                <th className="text-right">Reassign</th>
              </tr>
            </thead>
            <tbody data-testid={`emp-leads-tab-${tab}`}>
              {(tab === "overview" ? leads : byStatus[tab]).length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No leads in this bucket</td></tr>
              )}
              {(tab === "overview" ? leads : byStatus[tab]).map((l) => (
                <tr key={l.id}>
                  <td><Link to={`/leads/${l.id}`} className="font-mono link-blue">{l.lead_number}</Link></td>
                  <td><span className="badge badge-blue">{l.service_type}</span></td>
                  <td className="font-semibold">{l.client_name}</td>
                  <td><StatusBadge status={l.status} /></td>
                  <td><span className="badge badge-gray text-[10px]">{l.sla_status}</span></td>
                  <td className="text-xs text-muted-foreground">{new Date(l.updated_at).toLocaleString()}</td>
                  <td className="text-right">
                    <select
                      className="input !py-1 !text-xs !w-[150px]"
                      value={reassign[l.id] || ""}
                      onChange={(e) => { setReassign((r) => ({ ...r, [l.id]: e.target.value })); reassignLead(l, e.target.value); }}
                      data-testid={`reassign-${l.lead_number}`}
                    >
                      <option value="">— Reassign to —</option>
                      {employees.filter((e) => e.id !== id).map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "logins" && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-border font-semibold flex items-center gap-2"><LogIn size={16} /> Login History (last 20)</div>
          <table className="table-clean">
            <thead><tr><th>When</th><th>Details</th></tr></thead>
            <tbody data-testid="login-history-body">
              {login_history.length === 0 ? (
                <tr><td colSpan={2} className="text-center text-muted-foreground py-6">No logins recorded yet</td></tr>
              ) : login_history.map((l) => (
                <tr key={l.id}>
                  <td className="text-sm">{new Date(l.timestamp).toLocaleString()}</td>
                  <td className="text-xs text-muted-foreground">{l.details || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "activity" && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-border font-semibold flex items-center gap-2"><Activity size={16} /> Recent Activity</div>
          <table className="table-clean">
            <thead><tr><th>Action</th><th>Details</th><th>When</th></tr></thead>
            <tbody data-testid="activity-history-body">
              {recent_activity.length === 0 ? (
                <tr><td colSpan={3} className="text-center text-muted-foreground py-6">No activity yet</td></tr>
              ) : recent_activity.map((a) => (
                <tr key={a.id}>
                  <td><span className="badge badge-blue">{permLabel(a.action)}</span></td>
                  <td className="text-sm text-muted-foreground max-w-md truncate">{a.details || "—"}</td>
                  <td className="text-xs text-muted-foreground">{new Date(a.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
